import React from 'react';
import { Upload, Library, Import, Sparkles, Loader2, Scissors, Eraser, CheckSquare, Square } from 'lucide-react';
import { FileItem } from '@/components/video-editor/FileItem';
import { findFileAnalysis, type EnhancerFile, type AnalysisResults } from '@/lib/enhancerMeta';

// Assets panel — the project bin rebuilt in the current design language.
// Entry points (Upload / Library / Casting import / AI generate) up top, then
// a card grid of FileItem tiles (reused for its transcript view/edit dialog,
// "Select Sections" segment insertion, and add-to-timeline menus).

interface AssetsPanelProps {
  files: EnhancerFile[];
  selectedFiles: Set<string>;
  analysisResults: AnalysisResults;
  renderMode: 'lambda' | 'direct';
  aiGeneratingJob: { type: string; status: string; progress: number } | null;
  // Selection
  onToggleSelect: (fileId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  // Entry points
  dropzone: { getRootProps: () => Record<string, any>; getInputProps: () => Record<string, any>; isDragActive: boolean };
  onOpenLibrary: () => void;
  onOpenImporter: () => void;
  onOpenAIGenerator: () => void;
  // File actions (wired through FileItem)
  onDragStart: (file: EnhancerFile) => void;
  onAddToTimeline?: (file: EnhancerFile) => void;
  onAddToTimelineWithOptions?: (file: EnhancerFile, options: { includeMask?: boolean; videoOnly?: boolean }) => void;
  onInsertText?: (text: string, file?: EnhancerFile, startTime?: number, endTime?: number, referenceText?: string) => void;
  onFileRemove: (fileId: string) => void;
  onTranscriptEdit: (fileId: string, segmentIndex: number, newText: string) => void;
  onSpeakerEdit: (fileId: string, segmentIndex: number, newSpeaker: string) => void;
  // Bulk actions
  onAddSelectedToTimeline?: () => void;
  onRemoveBackground: (fileIds: string[]) => void;
}

export const AssetsPanel: React.FC<AssetsPanelProps> = ({
  files,
  selectedFiles,
  analysisResults,
  renderMode,
  aiGeneratingJob,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  dropzone,
  onOpenLibrary,
  onOpenImporter,
  onOpenAIGenerator,
  onDragStart,
  onAddToTimeline,
  onAddToTimelineWithOptions,
  onInsertText,
  onFileRemove,
  onTranscriptEdit,
  onSpeakerEdit,
  onAddSelectedToTimeline,
  onRemoveBackground,
}) => {
  // Mask files are shown via their linked original, not as standalone tiles.
  const visibleFiles = files.filter(f => f.layerType !== 'mask');
  const selectedVisible = visibleFiles.filter(f => selectedFiles.has(f.id));
  const selectedVideoIds = selectedVisible.filter(f => f.fileType === 'video').map(f => f.id);
  const allSelected = visibleFiles.length > 0 && selectedVisible.length === visibleFiles.length;

  // Merge matched analysis into each file so FileItem's transcript features work
  // even after analysisData was stripped for persistence.
  const withAnalysis = (file: EnhancerFile): EnhancerFile => {
    if ((file.analysisData as any)?.transcript) return file;
    const matched = findFileAnalysis(analysisResults, file.id);
    return matched ? { ...file, analysisData: matched } : file;
  };

  const entryButton = (label: string, icon: React.ReactNode, onClick?: () => void, extraProps?: Record<string, any>) => (
    <button
      type="button"
      onClick={onClick}
      {...(extraProps || {})}
      className="inline-flex items-center justify-center gap-1.5"
      style={{
        padding: '8px 10px',
        borderRadius: 10,
        border: '1px solid var(--hj-hairline)',
        background: 'var(--hj-surface-2)',
        color: 'var(--hj-ink)',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <section
      style={{
        padding: 18,
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-surface-1)',
        border: '1px solid var(--hj-hairline)',
      }}
    >
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
          Assets ({visibleFiles.length})
        </span>
        {visibleFiles.length > 0 && (
          <button
            type="button"
            onClick={allSelected ? onDeselectAll : onSelectAll}
            className="inline-flex items-center gap-1"
            style={{
              marginLeft: 'auto',
              padding: '3px 8px',
              borderRadius: 999,
              border: '1px solid var(--hj-hairline)',
              background: 'transparent',
              color: 'var(--hj-ink-2)',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {allSelected ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {/* Entry points */}
      <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
        <div {...dropzone.getRootProps()} style={{ width: '100%' }}>
          <input {...dropzone.getInputProps()} />
          {entryButton(dropzone.isDragActive ? 'Drop files…' : 'Upload', <Upload className="w-3.5 h-3.5" />)}
        </div>
        {entryButton('Library', <Library className="w-3.5 h-3.5" />, onOpenLibrary)}
        {entryButton('Hey Joe import', <Import className="w-3.5 h-3.5" />, onOpenImporter)}
        {entryButton(
          aiGeneratingJob && aiGeneratingJob.status !== 'completed' && aiGeneratingJob.status !== 'failed'
            ? `Generating ${aiGeneratingJob.type}…`
            : 'AI generate',
          aiGeneratingJob && aiGeneratingJob.status !== 'completed' && aiGeneratingJob.status !== 'failed'
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Sparkles className="w-3.5 h-3.5" />,
          onOpenAIGenerator,
        )}
      </div>

      {/* Selection hint for AI mode */}
      {renderMode === 'lambda' && visibleFiles.length > 0 && (
        <p className="m-0" style={{ marginBottom: 10, fontSize: 11.5, color: 'var(--hj-ink-3)', lineHeight: 1.45 }}>
          Checked assets are included in the AI edit.
        </p>
      )}

      {/* Bulk actions */}
      {selectedVisible.length > 0 && (
        <div className="flex flex-wrap gap-2" style={{ marginBottom: 12 }}>
          {renderMode === 'direct' && onAddSelectedToTimeline && (
            <button
              type="button"
              onClick={onAddSelectedToTimeline}
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hj-blue)',
                background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)',
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <Scissors className="w-3.5 h-3.5" />
              Add to timeline ({selectedVisible.length})
            </button>
          )}
          {selectedVideoIds.length > 0 && (
            <button
              type="button"
              onClick={() => onRemoveBackground(selectedVideoIds)}
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hj-hairline)',
                background: 'transparent', color: 'var(--hj-ink-2)',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <Eraser className="w-3.5 h-3.5" />
              Remove BG ({selectedVideoIds.length})
            </button>
          )}
        </div>
      )}

      {/* Asset grid */}
      {visibleFiles.length === 0 ? (
        <div
          className="text-center"
          style={{
            padding: '32px 16px',
            borderRadius: 10,
            border: '1.5px dashed var(--hj-hairline)',
            color: 'var(--hj-ink-3)',
          }}
        >
          <Upload className="w-7 h-7 mx-auto" style={{ marginBottom: 8 }} />
          <p className="m-0" style={{ fontSize: 13 }}>
            No assets yet — upload footage, pull from your library, or import takes from Hey Joe.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {visibleFiles.map(file => (
            <FileItem
              key={file.id}
              file={withAnalysis(file) as any}
              isSelected={selectedFiles.has(file.id)}
              onSelect={() => onToggleSelect(file.id)}
              onDragStart={() => onDragStart(file)}
              onAddToTimeline={onAddToTimeline ? () => onAddToTimeline(file) : undefined}
              onAddToTimelineWithOptions={onAddToTimelineWithOptions ? (options) => onAddToTimelineWithOptions(file, options) : undefined}
              viewMode="grid"
              onInsertText={onInsertText as any}
              onFileRemove={onFileRemove}
              onTranscriptEdit={onTranscriptEdit}
              onSpeakerEdit={onSpeakerEdit}
            />
          ))}
        </div>
      )}
    </section>
  );
};
